import { Association } from "./types";

export type AssociationCategory = "report" | "facility" | "submission";

export type AssociationCategoryState =
  | { kind: "none" }
  | { kind: "single"; category: AssociationCategory }
  | { kind: "mixed"; categories: AssociationCategory[] };

export const findAssociationIndex = (
  associations: Association[],
  association: Omit<Association, "amount">
) => {
  if (association.submissionId) {
    return associations.findIndex(
      (item) => item.submissionId === association.submissionId && !item.reportId && !item.facilityId
    );
  }

  if (association.reportId) {
    return associations.findIndex(
      (item) => item.reportId === association.reportId && !item.facilityId && !item.submissionId
    );
  }

  if (association.facilityId) {
    return associations.findIndex(
      (item) => item.facilityId === association.facilityId && !item.reportId && !item.submissionId
    );
  }

  return -1;
};

export const getAssociationCategory = (
  association: Association | null | undefined
): AssociationCategory | null => {
  if (!association) {
    return null;
  }

  const categories: AssociationCategory[] = [];

  if (association.reportId) {
    categories.push("report");
  }

  if (association.facilityId) {
    categories.push("facility");
  }

  if (association.submissionId) {
    categories.push("submission");
  }

  return categories.length === 1 ? categories[0] : null;
};

export const deriveAssociationCategoryState = (
  associations: Association[] | null | undefined
): AssociationCategoryState => {
  const categorySet = new Set<AssociationCategory>();

  (associations ?? []).forEach((association) => {
    const category = getAssociationCategory(association);

    if (category) {
      categorySet.add(category);
    }
  });

  if (categorySet.size === 0) {
    return { kind: "none" };
  }

  if (categorySet.size === 1) {
    const singleCategory = [...categorySet][0];
    return { kind: "single", category: singleCategory };
  }

  const orderedCategories: AssociationCategory[] = ["report", "facility", "submission"];

  return {
    kind: "mixed",
    categories: orderedCategories.filter((category) => categorySet.has(category)),
  };
};
